import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { notificationEventsAPI } from '../../../services/api';
import { getRoles, Role } from '../../../services/rbac';
import {
  NotificationEvent,
  NotificationRoleSummary,
} from '../../../types/notification';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { extractErrorMessage } from '../../../utils/errorHandler';

interface RowState {
  selected: NotificationRoleSummary[];
  saving: boolean;
  dirty: boolean;
}

const NotificationSettings: React.FC = () => {
  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rowState, setRowState] = useState<Record<number, RowState>>({});
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const [eventsRes, rolesRes] = await Promise.all([
        notificationEventsAPI.list(),
        getRoles(),
      ]);
      setEvents(eventsRes.items);
      setRoles(rolesRes);
      const initial: Record<number, RowState> = {};
      eventsRes.items.forEach((e) => {
        initial[e.id] = {
          selected: e.roles.slice(),
          saving: false,
          dirty: false,
        };
      });
      setRowState(initial);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roleOptions = useMemo<NotificationRoleSummary[]>(
    () =>
      roles
        .filter((r) => !!r.id && !!r.name)
        .map((r) => ({ id: r.id as number, name: r.name })),
    [roles],
  );

  const handleChange = (
    event: NotificationEvent,
    value: NotificationRoleSummary[],
  ) => {
    setRowState((prev) => ({
      ...prev,
      [event.id]: {
        ...(prev[event.id] || { selected: [], saving: false, dirty: false }),
        selected: value,
        dirty: true,
      },
    }));
  };

  const handleSave = async (event: NotificationEvent) => {
    const state = rowState[event.id];
    if (!state) return;
    setRowState((prev) => ({
      ...prev,
      [event.id]: { ...state, saving: true },
    }));
    try {
      const updated = await notificationEventsAPI.update(
        event.id,
        state.selected.map((r) => r.id),
      );
      setEvents((prev) => prev.map((e) => (e.id === event.id ? updated : e)));
      setRowState((prev) => ({
        ...prev,
        [event.id]: {
          selected: updated.roles.slice(),
          saving: false,
          dirty: false,
        },
      }));
      showSuccess('Destinataires mis à jour');
    } catch (err: any) {
      setRowState((prev) => ({
        ...prev,
        [event.id]: { ...state, saving: false },
      }));
      showError(extractErrorMessage(err));
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Paramètres des notifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choisissez les rôles qui reçoivent chaque type de notification.
        </Typography>
      </Box>

      <Paper>
        <TableContainer sx={getModuleTableTheme('administration')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={modernCellStyles}>Évènement</TableCell>
                <TableCell sx={modernCellStyles}>Description</TableCell>
                <TableCell sx={modernCellStyles}>Rôles destinataires</TableCell>
                <TableCell sx={modernCellStyles} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Aucun évènement configuré
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                events.map((event) => {
                  const state = rowState[event.id] || {
                    selected: [],
                    saving: false,
                    dirty: false,
                  };
                  return (
                    <TableRow key={event.id}>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {event.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {event.slug}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" color="text.secondary">
                          {event.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 320 }}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={roleOptions}
                          value={state.selected}
                          onChange={(_, value) => handleChange(event, value)}
                          getOptionLabel={(o) => o.name}
                          isOptionEqualToValue={(o, v) => o.id === v.id}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                size="small"
                                label={option.name}
                                {...getTagProps({ index })}
                                key={option.id}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Sélectionner des rôles" />
                          )}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            state.saving ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          disabled={!state.dirty || state.saving}
                          onClick={() => handleSave(event)}
                        >
                          Enregistrer
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />
    </Box>
  );
};

export default NotificationSettings;
